import { TabSchema, User } from '../GlobalTypes';
import type { SurveyTypeKey } from '../../shared/surveys';
import { PharmacyIntegrationType } from '../pharmacy/pharmacyTypes';

export interface Owner extends User {
  name?: string;
  is_online?: boolean;
  last_login_at: string | null;
  created_at: string;
  modified_at: string | null;
  phone_number?: string | null;
  pin?: string | null;
  formatted_address?: string | null;
  long_format_dob?: string | null;
  hub_connectivity_status?: string | null;
  device_model_number?: string | null;
  device_created_at?: string | null;
  pharmacy_phone?: string | null;
  pharmacy_address1?: string | null;
  pharmacy_address2?: string | null;
  pharmacy_city?: string | null;
  pharmacy_state?: string | null;
  pharmacy_zipcode?: string | null;
  // Returned by GET /customer/:customer_id so the selected user's own pharmacy
  // can be used without fetching the full system-wide pharmacy list.
  pharmacy_id?: number | null;
  pharmacy_integration_type_id?: PharmacyIntegrationType | null;
  wing?: string | null;
  room_number?: string | null;
}

export interface Careteam extends User {
  created_at?: string;
  patient_customer_id: number;
  careteam_role_id: number;
  phone_number?: string | null;
  is_online?: boolean;
}

export type CustomerMedication = {
  medication_dose_id: number;
  customer_id: number;
  national_drug_code: string;
  modified_at: string | null;
  drug_name: string;
  tablet_count: string;
  prescribed_datetime: string;
  dispensed_at: string | null;
  medication_dose_status_id: number;
  administered_at: string | null;
  administered_by: null | number;
  administered_timezone: string | null;
  batch_id: number;
  bag_number: number;
  created_at: string;
  medication_dose_status: string | undefined;
  first_name: string;
  last_name: string;
  name: string;
  unique_parent_id?: number;
  desired_admin_time: string;
  device_tz: string;
  event_type_id: number;
  event_type: string;
};

export interface CareteamInvite {
  first_name: string;
  last_name: string;
  email: string;
  careteam_role_id: string;
  expires_at: string;
}

export interface MedChangesPayload {
  batch_id: number;
  bag_number: number;
  desired_admin_time?: string;
  event_type_id?: number;
}

export type CustomerIssue = {
  id: number;
  title: string;
  created_at: string;
  resolved_at: string | null;
  severity: 'high' | 'low' | 'medium';
  customer_id: number; // <--  for caching
};

export type CustomerSurveyResponse = {
  survey_type_id: number;
  survey_type: SurveyTypeKey | null;
  // Survey question as stored on the survey type (OI-1769).
  title?: string;
  score: number;
  response: string;
  received_at: string;
  // Whether the patient agreed to be contacted about this answer. null / absent
  // means the flag was not collected for that answer (not a "no").
  can_reach_out?: boolean | null;
  customer_id?: number; // <--  for caching
};

// GET /customers/:id/satisfaction_survey rows as cached in the store: the row
// array spread into an object plus the customerId they were fetched for
// (same shape the Issues panel uses).
export type CachedCustomerSurveyResponses =
  | CustomerSurveyResponse[]
  | ({ customerId: number } & { [index: number]: CustomerSurveyResponse });

// The survey rows (all survey types) for one customer over one rolling
// window, as shown on the patient Insights tab. `requestedDays` is the API's
// `days` argument (as on OwnerSurveyInsightsState), so the tab can tell
// whether the cached rows are for its current window. Kept separate from the
// per-type Overview slots so the two screens never fight over one cache.
export type CustomerSurveyHistory = {
  customerId: number;
  requestedDays: number;
  rows: CustomerSurveyResponse[];
};

export enum CustomerStoreActionTypes {
  SET_LAST_REFRESH = 'SET_LAST_REFRESH',
  SET_IS_REFRESHING = 'SET_IS_REFRESHING',
  POPULATE_CUSTOMERS = 'POPULATE_CUSTOMERS',
  CUSTOMERS_API_ERR = 'CUSTOMERS_API_ERR',
  CUSTOMERS_SET_LOAD_STATE = 'CUSTOMERS_SET_LOAD_STATE',
  SUCCESS_CREATE_USER = 'SUCCESS_CREATE_USER',
  POPULATE_CUSTOMER = 'POPULATE_CUSTOMER',
  POPULATE_CUSTOMER_CARE_TEAM = 'POPULATE_CUSTOMER_CARE_TEAM',
  CLEAR_CUSTOMER_AND_CARE_TEAM = 'CLEAR_CUSTOMER_AND_CARE_TEAM',
  POPULATE_CUSTOMER_MEDICATION_TIME = 'POPULATE_CUSTOMER_MEDICATION_TIME',
  CREATE_PRIMARY_CONTACT_SUCCESS = 'CREATE_PRIMARY_CONTACT_SUCCESS',
  POPULATE_CARE_TEAM_DETAILS = 'POPULATE_CARE_TEAM_DETAILS',
  CLEAR_CARE_TEAM_DETAILS = 'CLEAR_CARE_TEAM_DETAILS',
  POPULATE_CARETEAM_INVITES = 'POPULATE_CARETEAM_INVITES',
  CUSTOMERS_SIDE_BAR_TAB_SELECTION = 'CUSTOMERS_SIDE_BAR_TAB_SELECTION',
  SAVE_OWNER_PIN = 'SAVE_OWNER_PIN',
  CREATE_MEDICATION_ROLL_SUCCESS = 'CREATE_MEDICATION_ROLL_SUCCESS',
  GET_MEDICATION_SCHEDULE_TEMPLATE = 'GET_MEDICATION_SCHEDULE_TEMPLATE',
  RESET_IMPORT_MED_ROLL_FORM = 'RESET_IMPORT_MED_ROLL_FORM',
  SUCCESS_DELETE_USER = 'SUCCESS_DELETE_USER',
  SET_ACTION_STATUS = 'SET_ACTION_STATUS',
  CLEAR_ACTION_STATUS = 'CLEAR_ACTION_STATUS',
  SET_PROFILE_PICTURE = 'SET_PROFILE_PICTURE',
  SET_PROFILE_PICTURE_LOADING = 'SET_PROFILE_PICTURE_LOADING',
  SET_PROFILE_PICTURE_ERROR = 'SET_PROFILE_PICTURE_ERROR',
  SET_MEDICATION_IMAGE_LOADING = 'SET_MEDICATION_IMAGE_LOADING',
  SET_MEDICATION_IMAGE = 'SET_MEDICATION_IMAGE',
  CLEAR_MEDICATION_IMAGE = 'CLEAR_MEDICATION_IMAGE',
  FETCH_CUSTOMER_ISSUES_SUCCESS = 'FETCH_CUSTOMER_ISSUES_SUCCESS',
  SET_CUSTOMER_ISSUES_LOADING = 'SET_CUSTOMER_ISSUES_LOADING',
  FETCH_CUSTOMER_SURVEY_SUCCESS = 'FETCH_CUSTOMER_SURVEY_SUCCESS',
  SET_CUSTOMER_SURVEY_LOADING = 'SET_CUSTOMER_SURVEY_LOADING',
  FETCH_CUSTOMER_WELLNESS_SUCCESS = 'FETCH_CUSTOMER_WELLNESS_SUCCESS',
  SET_CUSTOMER_WELLNESS_LOADING = 'SET_CUSTOMER_WELLNESS_LOADING',
  CUSTOMER_SURVEY_HISTORY_REQUEST = 'CUSTOMER_SURVEY_HISTORY_REQUEST',
  FETCH_CUSTOMER_SURVEY_HISTORY_SUCCESS = 'FETCH_CUSTOMER_SURVEY_HISTORY_SUCCESS',
  FETCH_CUSTOMER_SURVEY_HISTORY_FAILURE = 'FETCH_CUSTOMER_SURVEY_HISTORY_FAILURE',
}

export type SetLastRefreshAction = {
  type: CustomerStoreActionTypes.SET_LAST_REFRESH;
};

export type SetIsRefreshingAction = {
  type: CustomerStoreActionTypes.SET_IS_REFRESHING;
  payload: boolean;
};

export type PopulateCustomerAction = {
  type: CustomerStoreActionTypes.POPULATE_CUSTOMER;
  payload: { customer: Owner };
};

export type PopulateCustomersAction = {
  type: CustomerStoreActionTypes.POPULATE_CUSTOMERS;
  payload: { customerList: Owner[] };
};

export type CustomerActionAPIError = {
  type: CustomerStoreActionTypes.CUSTOMERS_API_ERR;
  payload: { error: any };
};

export type CustomerActionSetLoadStateAction = {
  type: CustomerStoreActionTypes.CUSTOMERS_SET_LOAD_STATE;
  payload: { isLoading: boolean };
};

export type CustomerCreateUserSuccessAction = {
  type: CustomerStoreActionTypes.SUCCESS_CREATE_USER;
  payload: { createUserSuccessful: boolean };
};

export type CustomerPopulateCareteamAction = {
  type: CustomerStoreActionTypes.POPULATE_CUSTOMER_CARE_TEAM;
  payload: { careTeam: { [key: string]: Careteam[] } };
};

export type CustomerClearCustomerAndCareteamAction = {
  type: CustomerStoreActionTypes.CLEAR_CUSTOMER_AND_CARE_TEAM;
};

export type CustomerCreatePrimaryContactAction = {
  type: CustomerStoreActionTypes.CREATE_PRIMARY_CONTACT_SUCCESS;
};

export type CustomerPopulateCareteamDetailAction = {
  type: CustomerStoreActionTypes.POPULATE_CARE_TEAM_DETAILS;
  payload: { careTeamDetails: Careteam };
};

export type CustomerClearCareteamAction = {
  type: CustomerStoreActionTypes.CLEAR_CARE_TEAM_DETAILS;
};

export type CustomerPopulateCareteamInvitesAction = {
  type: CustomerStoreActionTypes.POPULATE_CARETEAM_INVITES;
  payload: { careteamInvites: CareteamInvite[] };
};

export type CustomerSwitchSidebarAction = {
  type: CustomerStoreActionTypes.CUSTOMERS_SIDE_BAR_TAB_SELECTION;
  payload: { selectedTab: TabSchema };
};

export type CustomerSaveOwnerPinAction = {
  type: CustomerStoreActionTypes.SAVE_OWNER_PIN;
  payload: { ownerPin: string | null };
};

export type CustomerDeleteUserSuccessAction = {
  type: CustomerStoreActionTypes.SUCCESS_DELETE_USER;
  payload: { deleteUserSuccessful: boolean };
};

export type SetActionStatusAction = {
  type: CustomerStoreActionTypes.SET_ACTION_STATUS;
  payload: Partial<CustomerActionStatus>;
};

export type ClearActionStatusAction = {
  type: CustomerStoreActionTypes.CLEAR_ACTION_STATUS;
};

export type SetProfilePictureAction = {
  type: CustomerStoreActionTypes.SET_PROFILE_PICTURE;
  payload: string | null;
};

export type SetProfilePictureLoadingAction = {
  type: CustomerStoreActionTypes.SET_PROFILE_PICTURE_LOADING;
  payload: boolean;
};

export type SetProfilePictureErrorAction = {
  type: CustomerStoreActionTypes.SET_PROFILE_PICTURE_ERROR;
  payload: string | null;
};

export type SetMedicationImageLoadingAction = {
  type: CustomerStoreActionTypes.SET_MEDICATION_IMAGE_LOADING;
  payload: boolean;
};

export type SetMedicationImageAction = {
  type: CustomerStoreActionTypes.SET_MEDICATION_IMAGE;
  payload: string | null; // image URL or null
};

export type ClearMedicationImageAction = {
  type: CustomerStoreActionTypes.CLEAR_MEDICATION_IMAGE;
};

export type FetchCustomerIssuesSuccessAction = {
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_ISSUES_SUCCESS;
  payload: CustomerIssue[];
};

export type SetCustomerIssuesLoadingAction = {
  type: CustomerStoreActionTypes.SET_CUSTOMER_ISSUES_LOADING;
  payload: boolean;
};

export type FetchCustomerSurveySuccessAction = {
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_SUCCESS;
  payload: CachedCustomerSurveyResponses;
};

export type SetCustomerSurveyLoadingAction = {
  type: CustomerStoreActionTypes.SET_CUSTOMER_SURVEY_LOADING;
  payload: boolean;
};

export type FetchCustomerWellnessSuccessAction = {
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_WELLNESS_SUCCESS;
  payload: CachedCustomerSurveyResponses;
};

export type SetCustomerWellnessLoadingAction = {
  type: CustomerStoreActionTypes.SET_CUSTOMER_WELLNESS_LOADING;
  payload: boolean;
};

// Survey history (Insights tab) is a single "latest wins" slot: each request
// carries a sequence id so a slow response for a previous patient cannot
// overwrite the rows of the patient currently shown.
export type CustomerSurveyHistoryRequestAction = {
  type: CustomerStoreActionTypes.CUSTOMER_SURVEY_HISTORY_REQUEST;
  payload: { customerId: number; requestId: number };
};

export type FetchCustomerSurveyHistorySuccessAction = {
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_HISTORY_SUCCESS;
  payload: CustomerSurveyHistory & { requestId: number };
};

export type FetchCustomerSurveyHistoryFailureAction = {
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_HISTORY_FAILURE;
  payload: { requestId: number; error: string };
};

export type CustomerStore = {
  owners: Owner[] | null;
  careGivers: Owner[] | null;
  error: any | null;
  isLoading: boolean;
  createUserSuccessful: boolean;
  careTeam: { [key: string]: Careteam[] } | null;
  careTeamDetails: Careteam | null;
  facilityCustomers: Owner[] | null;
  didAddPrimaryContact: boolean;
  selectedTab: TabSchema;
  careteamInvites: CareteamInvite[] | null;
  currentOwnerPin: string | null;
  deleteUserSuccessful: boolean;
  lastRefreshTime: string | null;
  isRefreshing: boolean;
  actionStatus: CustomerActionStatus;
  profilePicture: string | null;
  profilePictureLoading: boolean;
  profilePictureError: string | null;
  medicationImage: string | null;
  medicationImageLoading: boolean;
  customerIssues: CustomerIssue[];
  customerIssuesLoading: boolean;
  customerSurveyResponses: CachedCustomerSurveyResponses;
  customerSurveyLoading: boolean;
  // Daily wellness check (DWS) rows — kept separate from CSAT so switching
  // panels never shows the other survey type's cached rows.
  customerWellnessResponses: CachedCustomerSurveyResponses;
  customerWellnessLoading: boolean;
  // Insights tab: survey rows for one customer and window (see CustomerSurveyHistory).
  customerSurveyHistory: CustomerSurveyHistory | null;
  customerSurveyHistoryLoading: boolean;
  customerSurveyHistoryError: string | null;
  customerSurveyHistoryRequestId: number;
};

export interface CustomerActionStatus {
  success: boolean;
  error: string | null;
}

export type CustomerStoreAction =
  | PopulateCustomerAction
  | PopulateCustomersAction
  | CustomerActionAPIError
  | CustomerActionSetLoadStateAction
  | CustomerCreateUserSuccessAction
  | CustomerPopulateCareteamAction
  | CustomerClearCustomerAndCareteamAction
  | CustomerCreatePrimaryContactAction
  | CustomerPopulateCareteamDetailAction
  | CustomerClearCareteamAction
  | CustomerPopulateCareteamInvitesAction
  | CustomerSwitchSidebarAction
  | CustomerSaveOwnerPinAction
  | CustomerDeleteUserSuccessAction
  | SetLastRefreshAction
  | SetIsRefreshingAction
  | SetActionStatusAction
  | ClearActionStatusAction
  | SetProfilePictureAction
  | SetProfilePictureLoadingAction
  | SetProfilePictureErrorAction
  | SetMedicationImageLoadingAction
  | SetMedicationImageAction
  | ClearMedicationImageAction
  | FetchCustomerIssuesSuccessAction
  | SetCustomerIssuesLoadingAction
  | FetchCustomerSurveySuccessAction
  | SetCustomerSurveyLoadingAction
  | FetchCustomerWellnessSuccessAction
  | SetCustomerWellnessLoadingAction
  | CustomerSurveyHistoryRequestAction
  | FetchCustomerSurveyHistorySuccessAction
  | FetchCustomerSurveyHistoryFailureAction;

export interface CreateCustomerPayload {
  first_name: string;
  last_name: string;
  username: string;
  facility_role_id: number;
  password: string;
  password2?: string;
  gender: number | '';
  pharmacy: number;
  address1: string;
  address2?: string;
  city: string;
  state: string;
}

export interface NewTechnicalSupportUserPayload {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  priviege_id: number;
  // Pharmacy techs (privilege_id 4) are tied to a pharmacy; omitted for other roles.
  pharmacy_id?: number;
}
